import {
	CSSProperties,
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { CalendarLayout } from "../../CalendarLayout/CalendarLayout";
import { EventCard, EventCardVariant } from "../../EventCard/EventCard";
import { IconValue } from "client/Components";
import {
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import styles from "./DayColumn.module.less";
import {
	DayHour,
	EventToRender,
	getDayHourRange,
	getEventsForDay,
	RegularEventToRender
} from "../getEventsForDay";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	CalendarAppointment,
	EditAppointmentInfo
} from "client/Features/Calendar/CalendarPage";
import { TimeIndicator } from "./TimeIndicator";
import { calendarColorToEventCardColorMap } from "../../Header/Header";
import { useShouldShowMeetingButton } from "client/Features/Calendar/hooks";
import { useAppointmentSpan } from "client/Features/Calendar/AppointmentSpan";
import { calculateTimeDifference } from "client/DateAndTime/dateTimeUtils";
import { Coordinate } from "client/Misc/commonTypes";
import { PlaceholderEventCard } from "../../EventCard/PlaceholderEventCard";
import { TimeOnly } from "client/DateAndTime/timeOnly";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { formatCalendarLocations } from "client/Features/Calendar/calendarUtils";
import { getClientId } from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";
import { AppointmentOrIdentifier } from "client/Features/Calendar/common";

export type DayColumnProps = {
	appointments: readonly CalendarAppointment[];
	day: Date;
	isToday: boolean;
	timezone: TimeZone;
	onOpenDetailsDialog(appointmentOrIdentifier: AppointmentOrIdentifier): void;
	onCreate: (
		coordinate: Coordinate,
		date: DateOnly,
		time: TimeOnly
	) => void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
};

export const DayColumn = (props: DayColumnProps): ReactElement => {
	const {
		appointments,
		day,
		isToday,
		timezone,
		onOpenDetailsDialog,
		onCreate,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId
	} = props;

	const events = useMemo(
		(): readonly EventToRender[] => getEventsForDay(
			appointments,
			day,
			timezone
		),
		[
			appointments,
			day,
			timezone
		]
	);

	const clickHandler = useCallback(
		(coordinate: Coordinate, time: TimeOnly): void => {
			onCreate(
				coordinate,
				convertToDateOnly(day, timezone),
				time
			);
		},
		[
			day,
			onCreate,
			timezone
		]
	);

	const hourRange = useMemo(
		(): readonly DayHour[] => getDayHourRange(),
		[]
	);

	return (
		<CalendarLayout.Column
			trackingId={generateTrackingId("day-column", `|${convertToDateOnly(day, timezone).toISOString()}`)}
			isToday={isToday}
		>
			{events.map(x => (
				<Event
					key={getClientId(x)}
					event={x}
					timezone={timezone}
					onOpenDetailsDialog={onOpenDetailsDialog}
					onEdit={onEdit}
					onAfterMutatingAppointment={onAfterMutatingAppointment}
					editingAppointmentId={editingAppointmentId}
				/>
			))}
			{hourRange.map((x: DayHour, i: number) => (
				<CalendarLayout.HourBox
					key={x.formattedTime}
					hourOfTheDay={i}
					onClick={clickHandler}
				/>
			))}
			{isToday &&
				<TimeIndicator timezone={timezone}/>
			}
		</CalendarLayout.Column>
	);
};
DayColumn.displayName = "DayColumn";

type EventProps = {
	event: EventToRender;
	timezone: TimeZone;
	onOpenDetailsDialog(appointmentOrIdentifier: AppointmentOrIdentifier): void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
};

const Event = (props: EventProps): ReactElement => {
	const {
		event,
		timezone,
		onOpenDetailsDialog,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId
	} = props;

	const clientId: Guid = getClientId(event);

	const {
		bottom,
		left,
		right,
		top
	} = event.position;

	const style: CSSProperties = {
		inset: `${top}% ${right}% ${bottom}% ${left}%`
	};

	return (
		<div
			key={clientId}
			className={styles.eventCard}
			style={style}
		>
			{event.kind === "placeholder"
				? (
					<PlaceholderEventCard
						trackingId={generateTrackingId("placeholder-event", `|${clientId}`)}
						color={isNotNil(event.color) ? calendarColorToEventCardColorMap[event.color] : undefined}
					/>
				) : (
					<RegularEventCard
						event={event}
						onOpenDetailsDialog={onOpenDetailsDialog}
						timezone={timezone}
						onEdit={onEdit}
						onAfterMutatingAppointment={onAfterMutatingAppointment}
						editingAppointmentId={editingAppointmentId}
						trackingId={generateTrackingId("event", `|${clientId}`)}
					/>
				)
			}
		</div>
	);
};

Event.displayName = "Event";

const reducedVisualDuration: number = 2;

type RegularEventCardProps = {
	event: RegularEventToRender;
	timezone: TimeZone;
	onOpenDetailsDialog: (appointmentOrIdentifier: AppointmentOrIdentifier) => void;
	onEdit: (
		coordinate: Coordinate,
		data: EditAppointmentInfo
	) => void;
	onAfterMutatingAppointment: (calendarIds: readonly string[]) => void;
	editingAppointmentId: string | undefined;
	trackingId: string;
};

const RegularEventCard = (props: RegularEventCardProps): ReactElement => {
	const {
		event,
		timezone,
		onOpenDetailsDialog,
		onEdit,
		onAfterMutatingAppointment,
		editingAppointmentId,
		trackingId
	} = props;

	const {
		id,
		subject,
		start,
		end,
		isAllDayEvent,
		isRecurring,
		hasAttachments,
		isPrivate,
		meeting,
		isCancelled,
		color,
		locations,
		myResponseStatus,
		isOrganizer,
		canEdit,
		calendarId
	} = event;

	const appointmentOrIdentifier = useMemo(
		(): AppointmentOrIdentifier => {
			if (isNotNil(id)) {
				return {
					id,
					calendarId
				};
			}

			return ({
				kind: "basic",
				subject,
				start,
				end,
				isAllDayEvent
			});
		},
		[
			end,
			id,
			isAllDayEvent,
			start,
			subject,
			calendarId
		]
	);

	const timeSpan: string = useAppointmentSpan(event, timezone);

	const shouldShowMeetingButton: boolean = useShouldShowMeetingButton(
		MINUTES_TO_SHOW_MEETING_BUTTON,
		event.start,
		event.end
	);

	const durationInMinutes: number = calculateTimeDifference(
		event.end,
		event.start,
		"minute"
	);

	const cardVisualHeightInMinutes: number = durationInMinutes - reducedVisualDuration;

	const cardVariant = useMemo(
		(): EventCardVariant => {
			if (cardVisualHeightInMinutes <= 25) {
				return "extra-small";
			} else if (cardVisualHeightInMinutes < 44) {
				return "small";
			} else {
				return "normal";
			}
		},
		[cardVisualHeightInMinutes]
	);

	const icons: IconValue[] = [];

	if (isPrivate) {
		icons.push("lock");
	}

	if (isRecurring) {
		icons.push("arrows-rotate");
	}

	if (hasAttachments) {
		icons.push("paperclip");
	}

	const locationText: string | null = formatCalendarLocations(locations);

	const tooltip: string = `${subject} \n${timeSpan} \n${locationText ?? ""}`;

	const canRespond: boolean = !isCancelled
		&& !isOrganizer
		&& canEdit
		&& (myResponseStatus?.isResponseOutstanding ?? false);

	return (
		<EventCard
			trackingId={trackingId}
			color={isNotNil(color) ? calendarColorToEventCardColorMap[color] : undefined}
			appointmentOrIdentifier={appointmentOrIdentifier}
			tooltip={tooltip}
			timezone={timezone}
			variant={cardVariant}
			onOpenDetailsDialog={() => onOpenDetailsDialog(appointmentOrIdentifier)}
			hasDashedBorder={canRespond}
			onEdit={onEdit}
			onAfterMutatingAppointment={onAfterMutatingAppointment}
			editingAppointmentId={editingAppointmentId}
			hasColoredIndicator
		>
			<EventCard.Info
				subject={subject}
				isCanceled={isCancelled}
				subtext={locationText ?? ""}
				cardVisualHeightInMinutes={cardVisualHeightInMinutes}
			/>

			{(meeting && shouldShowMeetingButton) && (
				<EventCard.JoinButton
					product={meeting.product}
					url={meeting.url}
					trackingId="join-button"
				/>
			)}
			<EventCard.Icons
				icons={icons}
			/>
		</EventCard>
	);
};
RegularEventCard.displayName = "RegularEventCard";

const MINUTES_TO_SHOW_MEETING_BUTTON: number = 15;
